import { useEffect, useRef, useState } from "react";
import { IconSend, IconX } from "@tabler/icons-react";
import logo from "../../../../assets/images/logo.svg";
import { Chip } from "../../../../components/common/chip";
import { Text } from "../../../../components/common/typography";
import { TypingIndicator } from "../../../ai-search/presentation/components/chat/typing-indicator";
import { parseChatResponse } from "../../../ai-search/utils/parse-chat-response";
import { ChatMarkdown } from "../../../ai-search/presentation/components/chat/chat-markdown";
import { WidgetRenderer } from "../../../ai-search/presentation/components/chat/widget-renderer";
import { useChat } from "../../../ai-search/hooks/useChat";

interface ProductChatPanelProps {
  open: boolean;
  onClose: () => void;
  productId?: number | null;
  productName: string;
  // Caller may still pass any of these; we ignore them now and rely on the BE
  // pinning the product (via productId) into the catalog context so the LLM
  // sees the authoritative datasheet/specs instead of stale FE-side text.
  // `variantId` is the exception — it IS used, because stock is per-variant.
  productDescription?: string | null;
  productSku?: string | null;
  productPrice?: number | null;
  productStock?: number | null;
  productBrand?: string | null;
  productCategory?: string | null;
  productImageUrl?: string | null;
  productNotes?: string | null;
  variantId?: number | null;
  /** First question to ask when the panel opens; defaults to a product overview. */
  initialQuestion?: string;
}

export function ProductChatPanel({
  open,
  onClose,
  productId,
  productName,
  variantId,
  initialQuestion,
}: ProductChatPanelProps) {
  // variantId matters for stock: the page shows the selected variant's
  // supplier_stock, so the assistant must be told which variant that is or it
  // reports the template-wide total instead.
  const { messages, responding, sendMessage, reset } = useChat({ productId, variantId });
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const openedRef = useRef(false);

  // No longer pushes page content — panel overlays as a wide drawer.

  // Bootstrap a fresh chat focused on this product when the panel first opens.
  useEffect(() => {
    if (open && !openedRef.current) {
      openedRef.current = true;
      reset();
      // Clean user-visible prompt. All "use the datasheet / no widgets / be
      // thorough" instructions live in the BE product-page system prompt
      // (triggered by productId in the request payload).
      void sendMessage(initialQuestion ?? `Tell me about "${productName}".`);
    }
    if (!open) openedRef.current = false;
  }, [open, productName, initialQuestion, reset, sendMessage]);

  // Auto-scroll to newest message.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, messages.at(-1)?.text]);

  const handleSend = () => {
    if (!input.trim()) return;
    // While a response is still streaming, this is queued and sent
    // automatically once it finishes (see useChat.sendMessage).
    void sendMessage(input.trim());
    setInput("");
  };

  const lastAssistantId = [...messages].reverse().find((m) => m.role === "assistant")?.id ?? null;

  return (
    <div
      className={`fixed right-0 bottom-0 z-50 flex flex-col border-l border-borderDefault bg-bgWhite shadow-[-4px_0_24px_rgba(0,0,0,0.08)] transition-all duration-300 ${open ? "w-[560px]" : "w-0 overflow-hidden"}`}
      style={{ top: getPanelTop() }}
    >
      {open && (
        <>
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-borderDefault px-5 py-4">
            <div className="flex min-w-0 flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <img src={logo} alt="Procurtal" className="h-4" />
                <Text variant="bodySmBold" tone="dark">AI</Text>
              </div>
              <div className="max-w-[320px] truncate">
                <Chip variant="tertiary">{productName}</Chip>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close chat"
              onClick={onClose}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-bgLight"
            >
              <IconX size={18} className="text-textMuted" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
            {messages.map((msg) => {
              if (msg.role === "user") {
                return (
                  <div key={msg.id} className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-brandPrimary px-4 py-2.5 text-textOnDark">
                      <Text variant="bodySm" tone="inherit">{msg.text}</Text>
                    </div>
                  </div>
                );
              }
              const isStreaming = responding && msg.id === lastAssistantId;
              const empty = !msg.text.trim();
              return (
                <div key={msg.id} className="flex justify-start">
                  {isStreaming && empty ? (
                    <TypingIndicator />
                  ) : (
                    <AssistantBubble
                      text={msg.text}
                      isStreaming={isStreaming}
                      onSendMessage={(t) => void sendMessage(t)}
                    />
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="shrink-0 border-t border-borderDefault px-5 py-4">
            <div className="flex gap-3 rounded-xl border border-borderDefault bg-bgLight/50 px-4 py-3 transition-colors focus-within:border-brandPrimary">
              <textarea
                rows={2}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask about this product…"
                className="flex-1 resize-none bg-transparent text-bodySm leading-snug text-textDark placeholder:text-textMuted focus:outline-none disabled:opacity-50"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim()}
                aria-label="Send message"
                className="self-end flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brandPrimary text-textOnDark transition-opacity disabled:opacity-40"
              >
                <IconSend size={15} />
              </button>
            </div>
            <div className="mt-2 text-center">
              <Text variant="bodyXs" tone="muted">Conversations are saved to My Conversations</Text>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Reads the top offset of #main-container once (before the panel is mounted).
// Falls back to 0 so the panel starts at the very top if no container is found.
function getPanelTop(): number {
  const main = document.getElementById("main-container");
  return main ? Math.round(main.getBoundingClientRect().top) : 0;
}

function AssistantBubble({
  text,
  isStreaming,
  onSendMessage,
}: {
  text: string;
  isStreaming: boolean;
  onSendMessage: (t: string) => void;
}) {
  const parsed = parseChatResponse(text, isStreaming);
  return (
    <div className="max-w-[90%] rounded-2xl rounded-tl-sm border border-borderLight bg-bgWhite px-4 py-3">
      {parsed.blocks.map((block, i) =>
        block.kind === "text" ? (
          <ChatMarkdown key={i} text={block.content} />
        ) : (
          <WidgetRenderer key={i} widgetType={block.widgetType} data={block.data} onSendMessage={onSendMessage} />
        ),
      )}
    </div>
  );
}
